/**
 * Workspace-wide artifact sort options + resolver.
 *
 * Lifted from
 * `pages/ProjectDetail/sections/Library/library.constants.ts` so the
 * picker (now in `entities/artifact/ui/`) and any other workspace-scoped
 * surface can consume the same sort list without crossing into a page
 * slice.
 *
 * The BE accepts `sortBy: 'name' | 'createdAt'` and uppercase
 * `sortOrder: 'ASC' | 'DESC'` for the workspace artifacts endpoint.
 * The parallel project-scoped sort (`ARTIFACT_SORT_CHOICES` /
 * `resolveArtifactSort`) lives in `library.constants.ts` because the
 * project-artifacts endpoint uses lowercase `'asc' | 'desc'` and a
 * different `sortBy` set — that asymmetry is a BE concern, not a FE one.
 */
import { i18n } from '@/shared/i18n';
import type { SortChipOption } from '@/shared/ui';

export type WorkspaceArtifactSortKey = 'name' | 'createdAt';
export type WorkspaceArtifactSortDirection = 'ASC' | 'DESC';

export interface WorkspaceArtifactSortChoice extends SortChipOption {
  sortBy: WorkspaceArtifactSortKey;
  sortOrder: WorkspaceArtifactSortDirection;
}

// Labels are getters so each read resolves against the active locale; a plain
// `i18n.t(...)` here would freeze the English string at import time.
export const WORKSPACE_ARTIFACT_SORT_CHOICES: ReadonlyArray<WorkspaceArtifactSortChoice> = [
  {
    id: 'createdAt-asc',
    get label() {
      return i18n.t('common:artifact.workspaceArtifactSort.oldestFirst');
    },
    sortBy: 'createdAt',
    sortOrder: 'ASC',
    direction: 'asc',
  },
  {
    id: 'name-asc',
    get label() {
      return i18n.t('common:artifact.workspaceArtifactSort.nameAZ');
    },
    sortBy: 'name',
    sortOrder: 'ASC',
    direction: 'asc',
  },
  {
    id: 'name-desc',
    get label() {
      return i18n.t('common:artifact.workspaceArtifactSort.nameZA');
    },
    sortBy: 'name',
    sortOrder: 'DESC',
    direction: 'desc',
  },
];

export function resolveWorkspaceArtifactSort(
  id: string | null,
): { sortBy: WorkspaceArtifactSortKey; sortOrder: WorkspaceArtifactSortDirection } | null {
  if (id === null) return null;
  const choice = WORKSPACE_ARTIFACT_SORT_CHOICES.find((c) => c.id === id);
  return choice ? { sortBy: choice.sortBy, sortOrder: choice.sortOrder } : null;
}
